import type { TestScriptCandidate } from '@/components/smartai/api/getScriptCandidates';
import {
  inferFolderAccent,
  segmentsUnderScriptsRoot,
  type FolderAccent,
} from '@/components/smartai/TestOptimization/scriptPathTree';

export type { FolderAccent };

export interface ScriptSelectionFileNode {
  type: 'file';
  name: string;
  filePath: string;
  fileName: string;
}

export interface ScriptSelectionFolderNode {
  type: 'folder';
  name: string;
  pathKey: string;
  accent: FolderAccent;
  /** All script file paths under this folder (recursive). */
  descendantPaths: string[];
  children: ScriptSelectionTreeNode[];
}

export type ScriptSelectionTreeNode =
  | ScriptSelectionFileNode
  | ScriptSelectionFolderNode;

type MutableFolder = {
  name: string;
  pathKey: string;
  dirs: Map<string, MutableFolder>;
  files: ScriptSelectionFileNode[];
};

function pathSegmentsForTree(filePath: string): string[] {
  const underScripts = segmentsUnderScriptsRoot(filePath);
  if (underScripts.length) return underScripts;
  return String(filePath ?? '')
    .trim()
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean);
}

function mutableToFolderNode(m: MutableFolder): ScriptSelectionFolderNode {
  const childFolders = [...m.dirs.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
  const childFiles = [...m.files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  const children: ScriptSelectionTreeNode[] = [
    ...childFolders.map(mutableToFolderNode),
    ...childFiles,
  ];

  const descendantPaths: string[] = [];
  for (const ch of children) {
    if (ch.type === 'file') descendantPaths.push(ch.filePath);
    else descendantPaths.push(...ch.descendantPaths);
  }

  return {
    type: 'folder',
    name: m.name,
    pathKey: m.pathKey,
    accent: inferFolderAccent(m.name),
    descendantPaths,
    children,
  };
}

/**
 * Build a nested folder + file tree from flat script candidates (under Scripts/ when present).
 */
export function buildSelectionTreeFromCandidates(
  candidates: TestScriptCandidate[],
): ScriptSelectionTreeNode[] {
  const hidden: MutableFolder = {
    name: '',
    pathKey: '',
    dirs: new Map(),
    files: [],
  };

  for (const c of candidates) {
    const segs = pathSegmentsForTree(c.filePath);
    if (!segs.length) continue;

    const fileName = segs[segs.length - 1] ?? c.fileName;
    const folderSegs = segs.slice(0, -1);
    const fileNode: ScriptSelectionFileNode = {
      type: 'file',
      name: fileName,
      filePath: c.filePath,
      fileName: c.fileName || fileName,
    };

    if (!folderSegs.length) {
      hidden.files.push(fileNode);
      continue;
    }

    let cur = hidden;
    for (const segment of folderSegs) {
      const nextPath =
        cur.pathKey === '' ? segment : `${cur.pathKey}/${segment}`;
      if (!cur.dirs.has(segment)) {
        cur.dirs.set(segment, {
          name: segment,
          pathKey: nextPath,
          dirs: new Map(),
          files: [],
        });
      }
      cur = cur.dirs.get(segment)!;
    }
    cur.files.push(fileNode);
  }

  const topFolders = [...hidden.dirs.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
  const topFiles = [...hidden.files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  return [...topFolders.map(mutableToFolderNode), ...topFiles];
}

/**
 * When every script sits under one top-level folder, show that folder's children
 * as the tree roots (folder view starts one level below the virtual root).
 */
export function displayTreeRoots(
  nodes: ScriptSelectionTreeNode[],
): ScriptSelectionTreeNode[] {
  let current = nodes;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const topFolders = current.filter(
      (n): n is ScriptSelectionFolderNode => n.type === 'folder',
    );
    const topFiles = current.filter(n => n.type === 'file');
    if (topFolders.length !== 1 || topFiles.length > 0) {
      return current;
    }
    const only = topFolders[0];
    if (!only.children.length) {
      return current;
    }
    current = only.children;
  }
}

/** Folder pathKeys at the visible top tier only (for initial collapsed view). */
export function collectTopLevelFolderKeys(
  nodes: ScriptSelectionTreeNode[],
): Set<string> {
  const keys = new Set<string>();
  for (const n of nodes) {
    if (n.type === 'folder' && n.children.length > 0) {
      keys.add(n.pathKey);
    }
  }
  return keys;
}

export function collectAllFolderPathKeys(
  nodes: ScriptSelectionTreeNode[],
): Set<string> {
  const keys = new Set<string>();
  const walk = (list: ScriptSelectionTreeNode[]) => {
    for (const n of list) {
      if (n.type === 'folder') {
        keys.add(n.pathKey);
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return keys;
}

export function collectExpandableFolderKeys(
  nodes: ScriptSelectionTreeNode[],
): Set<string> {
  const keys = new Set<string>();
  const walk = (list: ScriptSelectionTreeNode[]) => {
    for (const n of list) {
      if (n.type === 'folder') {
        const hasSubfolder = n.children.some(ch => ch.type === 'folder');
        if (hasSubfolder) {
          keys.add(n.pathKey);
        }
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return keys;
}

export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

export function folderCheckState(
  descendantPaths: string[],
  selectedPaths: Set<string>,
): CheckState {
  if (!descendantPaths.length) return 'unchecked';
  let selected = 0;
  for (const p of descendantPaths) {
    if (selectedPaths.has(p)) selected += 1;
  }
  if (selected === 0) return 'unchecked';
  if (selected === descendantPaths.length) return 'checked';
  return 'indeterminate';
}

export function fileCheckState(
  filePath: string,
  selectedPaths: Set<string>,
): boolean {
  return selectedPaths.has(filePath);
}

export function applyFolderToggle(
  descendantPaths: string[],
  selectedPaths: Set<string>,
  checked: boolean,
): Set<string> {
  const next = new Set(selectedPaths);
  for (const p of descendantPaths) {
    if (checked) next.add(p);
    else next.delete(p);
  }
  return next;
}

export function applyFileToggle(
  filePath: string,
  selectedPaths: Set<string>,
  checked: boolean,
): Set<string> {
  const next = new Set(selectedPaths);
  if (checked) next.add(filePath);
  else next.delete(filePath);
  return next;
}

function nodeMatchesFilter(node: ScriptSelectionTreeNode, q: string): boolean {
  if (node.type === 'file') {
    return (
      node.name.toLowerCase().includes(q) ||
      node.filePath.toLowerCase().includes(q)
    );
  }
  return (
    node.name.toLowerCase().includes(q) ||
    node.pathKey.toLowerCase().includes(q)
  );
}

function filterNode(
  node: ScriptSelectionTreeNode,
  q: string,
): ScriptSelectionTreeNode | null {
  if (node.type === 'file') {
    return nodeMatchesFilter(node, q) ? node : null;
  }

  const filteredChildren = node.children
    .map(ch => filterNode(ch, q))
    .filter((ch): ch is ScriptSelectionTreeNode => ch !== null);

  if (nodeMatchesFilter(node, q) || filteredChildren.length > 0) {
    const descendantPaths: string[] = [];
    for (const ch of filteredChildren) {
      if (ch.type === 'file') descendantPaths.push(ch.filePath);
      else descendantPaths.push(...ch.descendantPaths);
    }
    return {
      ...node,
      children: filteredChildren,
      descendantPaths,
    };
  }
  return null;
}

/** Keep folders/files matching filter (and ancestors of matches). */
export function filterSelectionTree(
  roots: ScriptSelectionTreeNode[],
  query: string,
): ScriptSelectionTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return roots;
  return roots
    .map(n => filterNode(n, q))
    .filter((n): n is ScriptSelectionTreeNode => n !== null);
}

export function collectFolderKeysForFilter(
  roots: ScriptSelectionTreeNode[],
): Set<string> {
  const keys = new Set<string>();
  const walk = (list: ScriptSelectionTreeNode[]) => {
    for (const n of list) {
      if (n.type === 'folder') {
        keys.add(n.pathKey);
        walk(n.children);
      }
    }
  };
  walk(roots);
  return keys;
}
